import moment from 'moment';
import { SHIFT_GAP_THRESHOLD_MINUTES } from 'containers/pages/scheduler/constants';

/**
 * Shift Calculator Utility
 *
 * Groups individual patrol assignments into logical "shifts" based on
 * time proximity. A shift is a series of patrols by the same guard
 * with less than SHIFT_GAP_THRESHOLD_MINUTES between them.
 */

/**
 * Calculates shifts from an array of patrols
 * A shift is defined as one or more sequential patrols by one guard,
 * with less than SHIFT_GAP_THRESHOLD_MINUTES gap between consecutive patrols.
 *
 * @param {Array} patrols - Array of patrol objects
 * @returns {Array} Array of shift objects
 */
export const calculateShifts = (patrols) => {
  if (!patrols || patrols.length === 0) {
    return [];
  }

  // Group patrols by guard (ContactId)
  const patrolsByGuard = patrols.reduce((acc, patrol) => {
    const contactId = patrol.ContactId;
    if (!acc[contactId]) {
      acc[contactId] = [];
    }
    acc[contactId].push(patrol);
    return acc;
  }, {});

  const allShifts = [];

  // Process each guard's patrols
  Object.entries(patrolsByGuard).forEach(([ContactId, guardPatrols]) => {
    // Sort patrols by start time
    const sortedPatrols = [...guardPatrols].sort((a, b) =>
      moment(a.StartTime).diff(moment(b.StartTime))
    );

    // Derive ContactName from the first patrol for display purposes
    const ContactName = sortedPatrols[0].ContactName;

    let currentShift = {
      patrols: [sortedPatrols[0]],
      ContactId,
      ContactName,
    };

    // Iterate through sorted patrols to detect shift boundaries
    for (let i = 1; i < sortedPatrols.length; i++) {
      const previousPatrol = sortedPatrols[i - 1];
      const currentPatrol = sortedPatrols[i];

      // Calculate gap between previous patrol's end and current patrol's start
      const gapMinutes = moment(currentPatrol.StartTime).diff(
        moment(previousPatrol.EndTime),
        'minutes'
      );

      // If gap is less than threshold, add to current shift
      if (gapMinutes < SHIFT_GAP_THRESHOLD_MINUTES) {
        currentShift.patrols.push(currentPatrol);
      } else {
        // Gap is >= threshold, finalize current shift and start new one
        allShifts.push(finalizeShift(currentShift));
        currentShift = {
          patrols: [currentPatrol],
          ContactId,
          ContactName,
        };
      }
    }

    // Add the last shift
    if (currentShift.patrols.length > 0) {
      allShifts.push(finalizeShift(currentShift));
    }
  });

  return allShifts;
};

/**
 * Finalizes a shift by calculating metadata
 * @param {Object} shift - Incomplete shift object with patrols array
 * @returns {Object} Complete shift object with metadata
 */
const finalizeShift = (shift) => {
  const { patrols, ContactId, ContactName } = shift;

  // Sort patrols by start time (should already be sorted, but ensure it)
  const sortedPatrols = [...patrols].sort((a, b) =>
    moment(a.StartTime).diff(moment(b.StartTime))
  );

  const firstPatrol = sortedPatrols[0];
  const lastPatrol = sortedPatrols[sortedPatrols.length - 1];

  // Get unique locations (addresses)
  const uniqueAddresses = [...new Set(patrols.map(p => p.AddressName))];

  // Get unique companies (customers)
  const uniqueCustomers = [...new Set(patrols.map(p => p.CustomerName))];

  // Calculate total duration in hours
  const totalDuration = moment(lastPatrol.EndTime).diff(
    moment(firstPatrol.StartTime),
    'hours',
    true
  );

  // Generate unique shift ID based on guard ID, start time, and patrol count
  const shiftId = `shift-${ContactId}-${moment(firstPatrol.StartTime).format('YYYY-MM-DD-HHmm')}-${patrols.length}`;

  return {
    id: shiftId,
    ContactId,
    ContactName,
    patrols: sortedPatrols,
    StartTime: firstPatrol.StartTime,
    EndTime: lastPatrol.EndTime,
    totalDuration: parseFloat(totalDuration.toFixed(2)),
    patrolCount: patrols.length,
    addresses: uniqueAddresses,
    addressCount: uniqueAddresses.length,
    customers: uniqueCustomers,
    customerCount: uniqueCustomers.length,
  };
};

/**
 * Finds the shift that contains a specific patrol
 * @param {Array} shifts - Array of shift objects
 * @param {Object} patrol - Patrol object to find
 * @returns {Object|null} The shift containing the patrol, or null if not found
 */
export const findShiftForPatrol = (shifts, patrol) => {
  if (!shifts || !patrol) {
    return null;
  }

  return shifts.find(shift =>
    shift.patrols.some(p => p.ScheduleAssignmentId === patrol.ScheduleAssignmentId)
  ) || null;
};

/**
 * Gets all shifts for a specific guard (contact)
 * @param {Array} shifts - Array of shift objects
 * @param {number|string} contactId - ID of the guard/contact
 * @returns {Array} Array of shifts for the guard
 */
export const getShiftsByGuard = (shifts, contactId) => {
  if (!shifts || !contactId) {
    return [];
  }

  return shifts.filter(shift => String(shift.ContactId) === String(contactId));
};
